import React, { useEffect, useState } from 'react'
import MUIModal from '../Modal/Modal'
import { IFile, IReport } from '../../helpers/Interfaces'
import DocumentButton from '../DocumentButton/DocumentButton'
import ApiService from '../../services/ApiService'
import Input from '../Input/Input'
import CopyIcon from "../../assets/images/Copy.svg";
import { getDocumentMedia } from '../../helpers/FirebaseFunctions'
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { Colors } from "../../helpers/Colors";
import { Checkbox } from '@mui/material'
import { getDateFromTimeStamp } from '../../helpers/HelperFunctions'
import { Popover } from 'antd';

import './GenerateLinkModal.scss'

type GenerateLinkModalProps = {
    open: boolean,
    close: CallableFunction,
    selectedDocument: IReport,
    loading: boolean
}

function GenerateLinkModal(props: Readonly<GenerateLinkModalProps>) {

    const apiService = new ApiService();

    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
    const [formData, setFormData] = useState<{ receivers: string, files: { fileId: string, fileName: string }[] }>
        ({ receivers: "", files: [] });
    const [generatedLink, setGeneratedLink] = useState("");
    const [media, setMedia] = useState([] as IFile[]);

    useEffect(() => {
        getMedia(props.selectedDocument.mediaId)
    }, [props.selectedDocument.mediaId])

    function handleMediaInit(docMedia: IFile[]) {
        const allowedExtensions = ['pdf', 'doc', 'docx'];

        const filteredMedia = docMedia.filter((file) => {
            const ext = file.fileName.split('.').pop()?.toLowerCase() || '';
            return allowedExtensions.includes(ext);
        });

        setMedia(filteredMedia);
    }

    async function getMedia(mediaId: string) {
        setLoading(true);
        await getDocumentMedia(mediaId, handleMediaInit)
        setLoading(false);
    }

    const handleClose = () => {
        setLoading(true);
        props.close("generateLink", false);
    }

    const closeAlert = () => {
        setShowAlert((prevDetails: any) => ({ ...prevDetails, open: false }));
    }

    function getReportTitle(report: IReport) {
        let title = "Share Report";

        if (report.reportName) {
            return `${title} of ${report.reportName}`;
        } else if (report.patientName && report.reportType && report.clinic) {
            return `${title} of ${report.patientName} - ${report.reportType} - ${report.clinic}`
        }

        return title;
    }

    function getReceivers(receivers: string): string[] {
        return receivers.trim().split(/\s+/);
    }

    function generateLink() {

        setLoading(true);

        if (formData.files.length === 0) {
            setShowAlert((prevDetails: any) => ({ ...prevDetails, isError: true, message: "Select File(s)", open: true }));
            setLoading(false);
            return;
        }

        if (formData.receivers?.trim() === "") {
            setShowAlert((prevDetails: any) => ({ ...prevDetails, isError: true, message: "Add Recipient(s)", open: true }));
            setLoading(false);
            return;
        }

        const data = {
            documentId: props.selectedDocument.id,
            receivers: getReceivers(formData.receivers),
            files: formData.files
        }

        apiService.generateLink(data).then((result) => {

            const data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert(newShowAlert);
                return;
            }

            setGeneratedLink(data?.link);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert(newShowAlert);
        }).finally(() => {
            setLoading(false);
        });
    }

    async function copyLinkToClipboard() {

        setLoading(true);

        try {
            await navigator.clipboard.writeText(generatedLink);

            let newShowAlert = showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Link Copied";
            newShowAlert.open = true;
            setShowAlert(newShowAlert);

            return;
        } catch (err) {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Failed to Copy Link";
            newShowAlert.open = true;
            setShowAlert(newShowAlert);
        } finally {
            setLoading(false);
        }
    }

    function isSelected(fileId: string) {
        return formData.files.some((file) => file.fileId === fileId);
    }

    function handleTick(file: IFile) {
        setFormData((prev) => {
            const exists = prev.files.some((f) => f.fileId === file.fileId);

            return {
                ...prev,
                files: exists
                    ? prev.files.filter((f) => f.fileId !== file.fileId)
                    : [...prev.files, { fileId: file.fileId, fileName: file.fileName }]
            };
        });
    }

    const renderPreviewContent = (file: IFile) => {
        const fileName = file.fileName.toLowerCase();
        const isPDF = fileName.endsWith('.pdf');
        const isDoc = fileName.endsWith('.doc') || fileName.endsWith('.docx');

        if (!isPDF && !isDoc) return null;

        const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(file.fileUrl)}&embedded=true`;

        return (
            <div className="file-hover-preview">
                <iframe src={isPDF ? file.fileUrl : viewerUrl} title="File Preview" width="400px" height="500px" />
            </div>
        );
    };

    return (
        <MUIModal showAlert={showAlert} closeAlert={closeAlert} open={props.open} close={handleClose}
            style={'generate-link-modal-card'} >
            <div className='generate-link-container'>
                <div className="generate-link-header">
                    <span className='title'>{getReportTitle(props.selectedDocument)}</span>
                    <span className='info'>Use Spaces to Separate Multiple Email Addresses</span>
                </div>
                <div className='generate-link-body'>
                    <div className='files-wrapper'>
                        {media.map((file) => {
                            return (<div className='row'>
                                <div className='left'>
                                    <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                                        checkedIcon={<img alt='check' src={BlueHomeDot} />}
                                        value="cryon" style={{ color: Colors.white_10 }}
                                        checked={isSelected(file.fileId)}
                                        onClick={(e) => {
                                            handleTick(file);
                                            e.stopPropagation();
                                        }} />
                                    <Popover placement="top" title={file.fileName} trigger="hover" mouseEnterDelay={0.3}
                                        content={
                                            <div className="file-hover-preview">
                                                {renderPreviewContent(file)}
                                            </div>
                                        }
                                        overlayStyle={{ zIndex: 1500 }}
                                    >
                                        <label style={{ cursor: 'pointer', position: 'relative' }} className="file-name">
                                            {file?.fileName}
                                        </label>
                                    </Popover>
                                </div>
                                <div className='right'>
                                    <span className="upload">{getDateFromTimeStamp(file?.fileId)}</span>
                                </div>
                            </div>)
                        })}
                    </div>
                    <div className='input-wrapper'>
                        <Input label='RECIPIENT(S)' value={formData.receivers} type='textarea' rows={5}
                            onChange={(value: string) => {
                                setFormData((prevData) => ({ ...prevData, receivers: value }));
                            }} />
                        {generatedLink ?
                            (<div className='copy-link'>
                                {generatedLink}
                                <img className="action" src={CopyIcon} alt={"Copy"} onClick={() => { copyLinkToClipboard(); }} />
                            </div>)
                            : (<></>)
                        }
                    </div>
                </div>
                <div className="generate-link-footer">
                    <DocumentButton width={200} text={"Generate Link"} onClick={generateLink} loading={loading} />
                </div>
            </div>
        </MUIModal>
    )
}

export default GenerateLinkModal